import { useEffect, useMemo, useState } from 'react';
import Carregando from '@/components/Carregando';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import FunilBarras from '@/components/dashboard/FunilBarras';
import GradeDiaria from '@/components/dashboard/GradeDiaria';
import PizzaOrigem from '@/components/dashboard/PizzaOrigem';
import ResultadoPorAnuncio from '@/components/dashboard/ResultadoPorAnuncio';
import MultiSelect from '@/components/MultiSelect';
import { analisar } from '@/dashboard/analise';
import { periodoPadrao, ultimos30Dias } from '@/dashboard/periodo';
import { ouvirAgendamentos } from '@/services/agendamentosService';
import { leadsComResgateNoMes, mesDe } from '@/leads/resgate';

/**
 * ★★ WK (29/set/2026) — O DASHBOARD É A VISÃO GERAL. Só ela.
 *
 * Saíram "Minha meta" e "Desempenho por pessoa" (D7). ★ Nada se perdeu: o
 * componente `MinhaMeta` continua existindo e o **Placar da equipe** o usa
 * inteiro, com Hoje/Semana/Mês e "Copiar meu relatório" — medido antes de tirar
 * (M5). Esta tela deixou de ser "o meu resumo + a empresa" para ser a empresa.
 *
 * A ordem de cima para baixo é a que o dono aprovou: filtros → funil → seis
 * cartões → dois painéis.
 */

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const porcento = (v) => `${Number(v ?? 0).toFixed(1).replace('.', ',')}%`;

/** ★ `null` é "não dá para saber" e aparece como travessão — nunca R$ 0. */
const moedaOuTraco = (v) => (v === null || v === undefined ? '—' : moeda(v));

/** `yyyy-mm-dd` → `Date` ao MEIO-DIA local (a armadilha de fuso da VC). */
const doCampo = (s, fimDoDia = false) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s ?? ''));
  if (!m) return null;
  const [, a, mes, d] = m;
  return fimDoDia
    ? new Date(Number(a), Number(mes) - 1, Number(d), 23, 59, 59, 999)
    : new Date(Number(a), Number(mes) - 1, Number(d), 0, 0, 0, 0);
};

/**
 * ★ A linha do painel "Onde os leads estão parados".
 *
 * ⚠ Aqui o trilho FICA — e a diferença com o funil é de significado, não de
 * gosto: esta é uma participação sobre um total (barra de proporção, o trilho é
 * o 100 %), enquanto o funil é um afunilamento entre degraus. Foi o desenho
 * aprovado que separou as duas leituras.
 *
 * ★ A cor diz a natureza da etapa: verde no que fechou, cinza no que se
 * encerrou sem venda, azul no resto.
 */
const COR_ETAPA = {
  fechado: '#026b3f',
  nao_compareceu: '#9aa4b2',
  nao_fechou: '#9aa4b2',
  nao_agendou: '#9aa4b2',
};

const BarraSerie = ({ id, rotulo, valor, maximo, participacao, titulo }) => {
  // ★ o mesmo piso de 2 % do funil, pela mesma razão.
  const largura = maximo > 0 ? Math.max(valor > 0 ? 2 : 0, (valor / maximo) * 100) : 0;
  return (
    <div className="etapa-linha" title={titulo}>
      <span className="etapa-rotulo">{rotulo}</span>
      <div className="etapa-trilho">
        <div
          className="etapa-marca"
          style={{ width: `${largura}%`, background: COR_ETAPA[id] ?? '#3b76e0' }}
        />
      </div>
      <span className="etapa-valor num">{valor} · {porcento(participacao)}</span>
    </div>
  );
};

export default function DashboardPage() {
  /**
   * ★★★ A-7a — `isGestor` ENTROU NESTA PÁGINA, e é a trava da EDIÇÃO da grade diária.
   *
   * ⚠⚠ A grade mostra meta, valor de vendas e ticket médio da clínica inteira, e tem campo
   * de EDIÇÃO de meta. ★ A rota `/dashboard` era `papeis={['gestor']}` (VU, temporário) — mas
   * essa trava era temporária por decisão de calendário, e no dia em que ela caísse a SDR
   * passaria a ver a grade sem ninguém pedir. ★★★ Por isso a trava ficou DENTRO da página,
   * onde ela não depende de uma linha que vai mudar.
   *
   * ★★★ A-7c (13/out/2026) — **O DIA CHEGOU, e a previsão acima pagou.** A rota abriu para
   * Vendas/SDR e **nada vazou**: a trava daqui só mudou de forma — de *"a grade não existe"*
   * para *"a grade existe em modo LEITURA"* (`somenteLeitura`, no fim desta página). ⚠ Se ela
   * estivesse na rota, abrir a rota teria aberto a edição junto.
   *
   * ⚠ `useAuth` LANÇA sem provider — foi o que obrigou o `wk.dashboard.render.test.js` a
   * ganhar o mock de `@/auth/AuthProvider`. Trabalho previsto, não defeito.
   */
  const { isGestor, isSuperadmin } = useAuth();
  const { clinicaId, leads, carregando, clinica } = useLeads();
  const [agendamentos, setAgendamentos] = useState([]);
  const [erro, setErro] = useState(null);

  // ---- filtros (a barra do topo) ----
  /**
   * ★★ O PADRÃO é do DIA 1 do mês corrente até HOJE (dono, 30/set) — não mais
   * "últimos 30 dias". A conta mora em `@/dashboard/periodo`, no fuso da
   * clínica: derivar de `toISOString()` abriria no mês errado nas horas da
   * virada. Ver o cabeçalho do módulo.
   */
  const [de, setDe] = useState(() => periodoPadrao().de);
  const [ate, setAte] = useState(() => periodoPadrao().ate);
  /**
   * ★ Os atalhos só PREENCHEM os dois campos; eles seguem editáveis.
   *
   * ★★ `aplicarMesAtual` chama **a mesma `periodoPadrao`** que decide a abertura
   * da tela — não uma cópia da conta. Se um dia a regra do padrão mudar, o botão
   * muda com ela, sozinho. Duas implementações do mesmo cálculo é exatamente como
   * a casa acabou com três definições de comparecimento.
   */
  const preencher = ({ de: d, ate: a }) => {
    setDe(d);
    setAte(a);
  };
  const aplicar30Dias = () => preencher(ultimos30Dias());
  const aplicarMesAtual = () => preencher(periodoPadrao());
  // ★ 'tudo' é o padrão porque é o que o dono pediu: "podemos ter agendamentos,
  // comparecimentos e fechamentos de leads de meses anteriores".
  const [escopo, setEscopo] = useState('tudo');
  // ★★ múltipla escolha (29/set): nenhuma marcada = todas.
  const [origens, setOrigens] = useState([]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    // Sem janela: a coleção inteira vem para a memória e o recorte é aqui — é o
    // que torna o filtro de datas livre sem consulta nem índice novo.
    return ouvirAgendamentos(clinicaId, {}, setAgendamentos, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir agendamentos:', e);
      setErro('Não foi possível carregar os agendamentos.');
    });
  }, [clinicaId]);

  const analise = useMemo(
    () => analisar({
      leads,
      agendamentos,
      inicio: doCampo(de),
      fim: doCampo(ate, true),
      escopo,
      origens,
      config: clinica?.config ?? null,
    }),
    [leads, agendamentos, de, ate, escopo, origens, clinica],
  );

  // ★ A lista do select de origem vem SEM o filtro aplicado — senão, ao escolher
  // uma origem, as outras sumiriam do próprio seletor e não haveria como voltar.
  const origensDisponiveis = useMemo(
    () => analisar({
      leads, agendamentos, inicio: doCampo(de), fim: doCampo(ate, true), escopo, config: clinica?.config ?? null,
    }).porOrigem,
    [leads, agendamentos, de, ate, escopo, clinica],
  );

  if (carregando) return <Carregando texto="Carregando o dashboard…" />;

  const { totais, taxas, porEtapa, porOrigem, maiorEtapa } = analise;
  const totalPizza = porOrigem.reduce((t, o) => t + o.leads, 0);

  const degraus = [
    { id: 'leads', rotulo: 'Leads', sub: 'entraram no período', valor: totais.leads, taxa: null },
    { id: 'agendados', rotulo: 'Agendaram', sub: 'marcaram avaliação', valor: totais.agendados, taxa: taxas.agendamento, verbo: 'agendaram' },
    { id: 'comparecimentos', rotulo: 'Compareceram', sub: 'sentaram na cadeira', valor: totais.comparecimentos, taxa: taxas.comparecimento, verbo: 'compareceram' },
    {
      id: 'fechamentos',
      rotulo: 'Fecharam',
      sub: 'contrato assinado',
      valor: totais.fechamentos,
      taxa: taxas.fechamento,
      verbo: 'fecharam',
      // ★★ quando passa de 100 %, a tela explica em vez de imprimir o impossível
      acima: taxas.fechamento > 100,
      foraDaJanela: analise.fechouSemComparecerNaJanela,
    },
  ];
  // ★ a linha de resumo do desenho, sob o funil
  const doLeadAoContrato = totais.leads > 0 ? (totais.fechamentos / totais.leads) * 100 : null;
  const naoCompareceram = porEtapa.find((e) => e.id === 'nao_compareceu')?.quantidade ?? 0;
  /**
   * ★★ ITEM 3 (30/set) — o resgate é do MÊS CORRENTE, e o rótulo passa a dizer.
   *
   * ⚠ Ele NÃO segue o filtro de datas, e não é descuido: medido em
   * `leads/resgate.js:5-7`, o campo guarda **chaves de mês**
   * (`resgates: ['2026-08','2026-09']`), não datas. Numa janela de 7 dias a
   * resposta honesta é "não sei" — qualquer número ali seria 0 (falso) ou o mês
   * inteiro (falso também). ★ Então a tela diz **o nome do mês**, que é o que
   * impede ler como "no período".
   *
   * ★ Pendência: para seguir o filtro, o campo precisaria guardar DATA. É
   * mudança de escrita e migração — outra peça.
   */
  const agora = new Date();
  const resgatesNoMes = leadsComResgateNoMes(leads, mesDe(agora)).length;
  const nomeDoMes = agora.toLocaleString('pt-BR', { month: 'long' });

  return (
    <div className="pagina">
      <header className="pagina-cabecalho">
        <div>
          <h1>Dashboard</h1>
          <p className="painel-sub">Visão geral do funil no período selecionado.</p>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      {/* 1 — FILTROS */}
      <section className="cartao filtros-dashboard">
        <div className="campo campo-datas">
          <span className="rotulo-campo">Período</span>
          <div className="linha-datas">
            <input id="db-de" type="date" aria-label="Data inicial" value={de} onChange={(e) => setDe(e.target.value)} />
            <span className="ate">até</span>
            <input id="db-ate" type="date" aria-label="Data final" value={ate} onChange={(e) => setAte(e.target.value)} />
            {/* ★ atalhos: só preenchem as duas datas, não travam o filtro */}
            <button type="button" id="db-mes-atual" className="btn-atalho" onClick={aplicarMesAtual}>
              Mês atual
            </button>
            <button type="button" id="db-30dias" className="btn-atalho" onClick={aplicar30Dias}>
              Últimos 30 dias
            </button>
          </div>
        </div>
        <div className="campo campo-escopo">
          <label htmlFor="db-escopo">O que contar</label>
          {/* ⚠ Os rótulos foram ENCURTADOS: no print do dono o seletor truncava
              em "Tudo que aconteceu no pe". O sentido está inteiro na frase de
              ajuda abaixo da barra, que explica os dois modos. */}
          <select id="db-escopo" value={escopo} onChange={(e) => setEscopo(e.target.value)}>
            <option value="tudo">Tudo que aconteceu</option>
            <option value="safra">Só quem entrou</option>
          </select>
        </div>
        <div className="campo campo-origens">
          <span className="rotulo-campo">Origem</span>
          {/* ★ Reusa o `MultiSelect` da VJ — o projeto já tinha um controle de
              caixinhas com teclado e clique-fora resolvidos. Inventar um segundo
              seria duas implementações do mesmo problema. */}
          <MultiSelect
            rotulo="Todas as origens"
            unidade="origem"
            plural="origens"
            grupos={[{ id: 'origens', rotulo: 'Origem do lead', opcoes: origensDisponiveis.map((o) => ({ id: o.id, rotulo: o.label })) }]}
            selecionados={origens}
            aoMudar={setOrigens}
          />
        </div>
        <p className="campo-ajuda filtros-nota">
          {escopo === 'tudo'
            ? 'O número de leads é sempre o de quem ENTROU no período; agendamentos, comparecimentos e fechamentos incluem leads de meses anteriores.'
            : 'Tudo restrito aos leads que entraram no período.'}
        </p>
      </section>

      {/* 2 — FUNIL */}
      <div className="cartao painel-funil">
        <div className="painel-cabecalho">
          <h2 className="painel-titulo">O funil</h2>
          {/* ★★ A nota "N vieram de safras anteriores" SAIU daqui (29/set): ela
              fala dos leads CONSIDERADOS, e os considerados não são os do funil
              — o primeiro degrau conta só quem ENTROU no período. Ficando aqui,
              ela sugeria que os 21 estavam dentro dos 667, e não estão. Foi para
              a linha dos dois painéis, que é de quem ela fala.
              ★ VR: o resgate continua aqui — é contexto do mês, e não entra em
              "Leads" nem em taxa nenhuma. */}
          <span className="painel-nota">
            os {totais.leads} leads que ENTRARAM no período
            {resgatesNoMes > 0 ? ` · ${resgatesNoMes} resgates em ${nomeDoMes}` : ''}
          </span>
        </div>

        {/* ★ A linha que o dono pediu: os dois números ao lado um do outro
            falam de coisas diferentes, e a dúvida dele era legítima. */}
        {(resgatesNoMes > 0 || analise.deSafrasAnteriores > 0) && (
          <p className="funil-diferenca">
            Os resgates voltaram a conversar; os {analise.deSafrasAnteriores} de meses anteriores
            voltaram e <strong>avançaram no funil</strong>.
          </p>
        )}

        <FunilBarras degraus={degraus} />

        <div className="funil-resumo">
          <span>
            <strong className="num">{doLeadAoContrato === null ? '—' : porcento(doLeadAoContrato)}</strong>
            {' '}do lead ao contrato
          </span>
          <span><strong className="num">{totais.perdidos}</strong> perdidos no caminho</span>
          <span><strong className="num">{naoCompareceram}</strong> não compareceram</span>
        </div>
        <p className="funil-explica">
          Quem pulou etapa conta nas de baixo: lead cadastrado já como fechado entra também como{' '}
          <strong>1 agendamento e 1 comparecimento</strong> — ele agendou, sentou na cadeira e fechou,
          mesmo que ninguém tenha registrado passo a passo.
        </p>
      </div>

      {/* 3 — SEIS CARTÕES. ★ Cada um tem a SUBLEGENDA que explica a conta: o
          número sozinho não diz de onde saiu, e é o que o desenho corrigiu. */}
      <div className="indicadores cartoes-wk">
        <div className="indicador">
          <span className="indicador-rotulo">Total de oportunidade</span>
          <span className="indicador-valor num">{moeda(totais.totalOportunidade)}</span>
          <span className="indicador-nota">orçado a quem compareceu</span>
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Ticket médio de oportunidade</span>
          <span className="indicador-valor num">{moedaOuTraco(totais.ticketOportunidade)}</span>
          <span className="indicador-nota">
            {totais.comparecimentos > 0
              ? `${moeda(totais.totalOportunidade)} ÷ ${totais.comparecimentos}`
              : 'ninguém compareceu no período'}
          </span>
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Valor fechado</span>
          <span className="indicador-valor num verde">{moeda(totais.valor)}</span>
          <span className="indicador-nota">total da venda digitado</span>
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Ticket médio da venda</span>
          <span className="indicador-valor num verde">{moedaOuTraco(totais.ticketVenda)}</span>
          <span className="indicador-nota">
            {totais.fechamentos > 0
              ? `${moeda(totais.valor)} ÷ ${totais.fechamentos}`
              : 'nenhum contrato no período'}
          </span>
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Conversão de valor</span>
          <span className="indicador-valor num">
            {analise.conversaoValor.taxa === null ? '—' : porcento(analise.conversaoValor.taxa)}
          </span>
          {/* ★ a barrinha do desenho: a taxa vista, não só lida */}
          {analise.conversaoValor.taxa !== null && (
            <div className="medidor">
              <div
                className="medidor-marca"
                style={{ width: `${Math.min(100, analise.conversaoValor.taxa)}%` }}
              />
            </div>
          )}
          <span className="indicador-nota">só quem tem orçamento</span>
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Fechamentos</span>
          <span className="indicador-valor num">{totais.fechamentos}</span>
          <span className="indicador-nota">
            {totais.comparecimentos > 0
              ? `de ${totais.comparecimentos} que compareceram`
              : 'ninguém compareceu no período'}
          </span>
        </div>
      </div>

      {/* 4 — DOIS PAINÉIS */}
      {/* ★★ 29/set — A LINHA QUE FALTAVA. O dono viu 667 no funil e 688 aqui
          embaixo e não tinha como saber que os dois estavam certos. Os painéis
          falam de um conjunto MAIOR: os que entraram no período MAIS os de
          safras anteriores que se mexeram nele. Agora a tela declara isso, uma
          vez, acima dos dois blocos. */}
      <p className="colunas-nota">
        Os dois quadros abaixo falam dos mesmos{' '}
        <strong>{analise.moveramNoPeriodo} leads que se moveram no período</strong>
        {/* ★ "safra" é jargão nosso — a equipe não entende (dono, 30/set). O TEXTO
            passou a dizer "meses anteriores"; a variável `deSafrasAnteriores` e o
            `value="safra"` do escopo continuam com o nome do CONCEITO. */}
        {analise.deSafrasAnteriores > 0 && (
          <> — <strong>{analise.deSafrasAnteriores} deles vieram de meses anteriores</strong>{' '}
            e avançaram agora, por isso o funil começa em {totais.leads}
          </>
        )}
        .
      </p>
      <div className="colunas-dashboard">
        <section className="cartao">
          <div className="painel-cabecalho">
            <h2 className="painel-titulo">Onde os leads estão parados</h2>
            <span className="painel-nota">{analise.moveramNoPeriodo} se moveram no período</span>
          </div>
          <div>
            {porEtapa.every((e) => e.quantidade === 0) ? (
              <p className="em-breve">Nenhum lead no período.</p>
            ) : (
              <div className="grafico-barras">
                {porEtapa.map((e) => (
                  <BarraSerie
                    key={e.id}
                    id={e.id}
                    rotulo={e.label}
                    valor={e.quantidade}
                    maximo={maiorEtapa}
                    participacao={e.participacao}
                    titulo={`${e.label}: ${e.quantidade} leads (${porcento(e.participacao)})`}
                  />
                ))}
              </div>
            )}
          </div>
          {/* ★★ 29/set — A EXPLICAÇÃO ANTIGA CAIU JUNTO COM A CAUSA. Aqui dizia
              "mostra a etapa de hoje" e "N fecharam fora do período". O painel
              deixou de contar a etapa de hoje: agora conta o EVENTO da janela, e
              "Fechou" é o mesmo conjunto do último degrau do funil. Não há mais
              diferença a justificar. */}
          <p className="painel-rodape">
            Cada lead conta pelo que <strong>aconteceu com ele no período</strong> — quem não se
            mexeu na janela não aparece aqui. &ldquo;Fechou&rdquo; é o mesmo número do último
            degrau do funil.
          </p>
        </section>

        {/* ★ Ao escolher uma origem este painel SOME (decisão do dono): com o
            filtro ligado ele mostraria uma rosca de 100 % e não diria nada. */}
        {origens.length === 0 && (
          <section className="cartao">
            <div className="painel-cabecalho">
              <h2 className="painel-titulo">De onde eles vêm</h2>
              <span className="painel-nota">{analise.moveramNoPeriodo} se moveram no período</span>
            </div>
            <PizzaOrigem fatias={porOrigem} total={totalPizza} destaque={analise.destaqueOrigem} />
          </section>
        )}
      </div>

      {/* ★★ PC-7 — RESULTADO POR ANÚNCIO. ★ Diferente da rosca, NÃO some com filtro de origem:
          filtrar "Meta Ads" para ver só os anúncios da Meta é justamente o uso. As linhas são os
          MESMOS conjuntos do funil (Σ = funil). ⚠ D5 do dono: o "De onde eles vêm" conta pela cesta,
          então os dois cartões podem não somar igual — a nota abaixo diz isso a quem lê. */}
      <section className="cartao" data-teste="resultado-por-anuncio">
        <div className="painel-cabecalho">
          <h2 className="painel-titulo">Resultado por anúncio</h2>
          <span className="painel-nota">pelo nome da frase pronta; sem frase, pelo título do anúncio da Meta</span>
        </div>
        <ResultadoPorAnuncio linhas={analise.porAnuncio} maximo={analise.maiorAnuncio} />
        <p className="painel-rodape">
          Conta como o funil acima: a soma das linhas é o funil. O cartão de origens conta quem se
          moveu no período, então os dois cartões podem não somar igual.
        </p>
      </section>

      {/* ★★★ A-7a — 5 (ÚLTIMO) — A GRADE DIÁRIA, só do gestor.
          ★ Fica no FIM de propósito (decisão do dono): o funil e os cartões respondem "como
          vai o período"; a grade responde "como foi cada dia contra a meta", que é leitura de
          conferência, não de abertura de tela.
          ⚠ Ela recebe os MESMOS `leads` e `agendamentos` que já estão em memória — nenhuma
          consulta nova, nenhum índice novo. */}
      {/**
        * ★★★ A-7c (13/out/2026) — A GRADE PASSOU A APARECER PARA QUEM ENTRA, em modo LEITURA.
        *
        * ⚠⚠ Antes era `{isGestor && …}`: a grade simplesmente não existia para mais ninguém.
        * ★ Com `/dashboard` aberto para Vendas/SDR, a SDR e o negociador precisam **ver** o mês
        * contra a meta e **copiar o resultado do dia** — e **não** podem editar meta.
        * ★★★ A trava virou `somenteLeitura`, e ela é por PAPEL, não por rota: ⚠ a rota pode
        * mudar outra vez (ela já mudou duas), e a regra "só o gestor edita" não muda com ela.
        * ★ `isSuperadmin` entra junto porque ele administra a clínica inteira — é o mesmo par
        * `isGestor || isSuperadmin` que a `IndicadoresPage` usa em `vejoTudo`.
        * ⚠ E as rules recusam a escrita de qualquer forma: a tela é a primeira camada, não a única.
        */}
      <GradeDiaria
        clinicaId={clinicaId}
        leads={leads}
        agendamentos={agendamentos}
        somenteLeitura={!(isGestor || isSuperadmin)}
        configDaClinica={clinica?.config ?? null}
      />
    </div>
  );
}
